/**
 * Uiverse.io by Smit-Prajapati — 3D glass card (adapted)
 * Product image is the dominant background; glass + text sit on top.
 * Hover (desktop) / press (touch) triggers the 3D rotate.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Heart, ShoppingCart, Star } from 'lucide-react';
import { cn } from '../../lib/utils';
import { AppLink } from '../shared/AppLink';
import './glass-3d-card.css';

export type Glass3dCardProps = {
  title: string;
  text: string;
  meta?: string;
  /** Full-bleed product photo (takes visual majority). */
  imageSrc?: string | null;
  /** Availability from product storage (`isAvailable`). */
  isAvailable?: boolean;
  availableLabel?: string;
  unavailableLabel?: string;
  viewMoreLabel?: string;
  onViewMore?: () => void;
  viewMoreHref?: string;
  isLiked?: boolean;
  isFavorite?: boolean;
  isInCart?: boolean;
  onLike?: (e: React.MouseEvent) => void;
  onFavorite?: (e: React.MouseEvent) => void;
  onCart?: (e: React.MouseEvent) => void;
  likeTitle?: string;
  favoriteTitle?: string;
  cartTitle?: string;
  className?: string;
  primaryAction?: React.ReactNode;
};

export function Glass3dCard({
  title,
  text,
  meta,
  imageSrc,
  isAvailable = true,
  availableLabel = 'Available',
  unavailableLabel = 'Unavailable',
  viewMoreLabel = 'View more',
  onViewMore,
  viewMoreHref,
  isLiked,
  isFavorite,
  isInCart,
  onLike,
  onFavorite,
  onCart,
  likeTitle,
  favoriteTitle,
  cartTitle,
  className,
  primaryAction,
}: Glass3dCardProps) {
  const [tilted, setTilted] = useState(false);
  const pointerIdRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const movedRef = useRef(false);

  const clearTilt = useCallback(() => {
    pointerIdRef.current = null;
    startRef.current = null;
    movedRef.current = false;
    setTilted(false);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Ignore presses on interactive controls — they shouldn't toggle tilt
    const target = e.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select')) return;

    pointerIdRef.current = e.pointerId;
    startRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;

    // Desktop mouse: hover/press open (leave closes)
    if (e.pointerType === 'mouse') setTilted(true);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (pointerIdRef.current !== e.pointerId || !startRef.current) return;
    const dx = Math.abs(e.clientX - startRef.current.x);
    const dy = Math.abs(e.clientY - startRef.current.y);
    // Finger jitter / page scroll / rail swipe — don't treat as a tap
    if (dx > 10 || dy > 10) movedRef.current = true;
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current) return;
    const isTouch = e.pointerType === 'touch' || e.pointerType === 'pen';
    const wasTap = !movedRef.current;
    pointerIdRef.current = null;
    startRef.current = null;

    // Touch/pen: tap toggles open; scroll/swipe must not latch tilt.
    if (isTouch && wasTap) {
      setTilted((v) => !v);
    }
  }, []);

  return (
    <div
      className={cn(
        'uiverse-glass3d-parent mx-auto',
        imageSrc && 'uiverse-glass3d-parent--photo',
        tilted && 'is-tilted',
        className
      )}
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') setTilted(true);
      }}
      onPointerLeave={(e) => {
        // Mobile fires leave after touch — must NOT clear latched tilt
        if (e.pointerType === 'mouse') clearTilt();
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        // Scroll gesture cancelled the press — drop tracking without forcing tilt closed
        pointerIdRef.current = null;
        startRef.current = null;
        movedRef.current = false;
      }}
    >
      <div className={cn('uiverse-glass3d-card', imageSrc && 'uiverse-glass3d-card--photo')}>
        {imageSrc ? (
          <div
            className="uiverse-glass3d-photo"
            style={{
              backgroundImage: `linear-gradient(165deg, rgba(12,28,16,0.12) 0%, rgba(12,28,16,0.22) 42%, rgba(12,28,16,0.62) 100%), url(${imageSrc})`,
            }}
            aria-hidden
          />
        ) : null}

        <div className="uiverse-glass3d-glass" />

        <div className="uiverse-glass3d-content">
          <span className="uiverse-glass3d-title">{title}</span>
          <span className="uiverse-glass3d-text">{text}</span>
          {meta ? <span className="uiverse-glass3d-meta">{meta}</span> : null}
          {primaryAction ? <div className="uiverse-glass3d-primary">{primaryAction}</div> : null}
        </div>

        <div className="uiverse-glass3d-bottom">
          <div className="uiverse-glass3d-social">
            {onLike ? (
              <button
                type="button"
                className={cn('uiverse-glass3d-social-btn', isLiked && 'is-active-like')}
                onClick={onLike}
                title={likeTitle}
                aria-label={likeTitle}
              >
                <Heart className={cn('h-3.5 w-3.5', isLiked && 'fill-current')} />
              </button>
            ) : null}
            {onFavorite ? (
              <button
                type="button"
                className={cn('uiverse-glass3d-social-btn', isFavorite && 'is-active-fav')}
                onClick={onFavorite}
                title={favoriteTitle}
                aria-label={favoriteTitle}
              >
                <Star className={cn('h-3.5 w-3.5', isFavorite && 'fill-current')} />
              </button>
            ) : null}
            {onCart ? (
              <button
                type="button"
                className={cn('uiverse-glass3d-social-btn', isInCart && 'is-active-cart')}
                onClick={onCart}
                title={cartTitle}
                aria-label={cartTitle}
              >
                <ShoppingCart className={cn('h-3.5 w-3.5', isInCart && 'fill-current')} />
              </button>
            ) : null}
          </div>

          <div className="uiverse-glass3d-view-more">
            {viewMoreHref ? (
              <AppLink to={viewMoreHref} className="uiverse-glass3d-view-more-button">
                {viewMoreLabel}
              </AppLink>
            ) : (
              <button type="button" onClick={onViewMore} className="uiverse-glass3d-view-more-button">
                {viewMoreLabel}
              </button>
            )}
            <svg className="uiverse-glass3d-chevron" viewBox="0 0 24 24" aria-hidden>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </div>
        </div>

        {/* Uiverse.io by Zain-Muhammad — availability pulse (top-right) */}
        <div
          className={cn(
            'uiverse-avail-btn',
            isAvailable ? 'uiverse-avail-btn--available' : 'uiverse-avail-btn--unavailable'
          )}
          title={isAvailable ? availableLabel : unavailableLabel}
          aria-label={isAvailable ? availableLabel : unavailableLabel}
          role="status"
        >
          <span className="uiverse-avail-circle" aria-hidden>
            <span className="uiverse-avail-dot" />
            <span className="uiverse-avail-outline" />
          </span>
          <span className="uiverse-avail-label">
            {isAvailable ? availableLabel : unavailableLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
